import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { apiClient } from '../utils/apiClient';

const OrganizationWebsite = ({ onOpenLocationSettings }) => {
  const { currentUser, locations = [], selectedLocationId, locationError } = useApp();
  const canManage = ['admin', 'superuser'].includes(currentUser?.user?.orgRole);
  const [locationChoice, setLocationChoice] = useState('');
  const [widget, setWidget] = useState(null);
  const [copyMessage, setCopyMessage] = useState('');
  const snippetRef = useRef(null);
  const location = locations.find(item => String(item.id) === locationChoice)
    || locations.find(item => String(item.id) === String(selectedLocationId))
    || locations.find(item => item.isDefault)
    || locations[0];
  const locationId = String(location?.id ?? '');
  const sessionId = currentUser?.sessionId;
  const requestKey = JSON.stringify([sessionId, currentUser?.user?.orgId, locationId]);

  useEffect(() => {
    if (!canManage || !sessionId || !locationId) return undefined;
    const controller = new AbortController();
    let active = true;
    setWidget({ requestKey, loading: true });
    setCopyMessage('');
    const loadWidget = async () => {
      try {
        const response = await apiClient(`/prices/widget?locationId=${encodeURIComponent(locationId)}`, {
          headers: { 'session-id': sessionId }, signal: controller.signal
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = response.status === 503
            ? 'Website feature not set up yet (database migration pending).'
            : data?.error || (response.status === 404 ? 'Location not found.' : 'Unable to load the prices widget.');
          if (active) setWidget({ requestKey, error, status: response.status });
          return;
        }
        if (typeof data?.snippet !== 'string' || !data.snippet.trim()) throw new Error('The prices widget response did not include an embed code.');
        if (active) setWidget({ requestKey, data });
      } catch (error) {
        if (active) setWidget({ requestKey, error: error.message || 'Unable to load the prices widget.' });
      }
    };
    loadWidget();
    return () => { active = false; controller.abort(); };
  }, [canManage, sessionId, locationId, requestKey]);

  if (!canManage) return null;

  const currentWidget = widget?.requestKey === requestKey ? widget : null;
  const snippet = currentWidget?.data?.snippet;
  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopyMessage('Embed code copied.');
    } catch {
      snippetRef.current?.focus();
      snippetRef.current?.select();
      setCopyMessage('Clipboard unavailable. The embed code is selected for copying.');
    }
  };
  const locationSettingsLink = <a href="/manage" onClick={event => {
    if (onOpenLocationSettings) { event.preventDefault(); onOpenLocationSettings(); }
  }}>Location settings</a>;

  return <section aria-label="Organization website settings" style={{ maxWidth: '960px', margin: '0 auto', minWidth: 0 }}>
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'end', gap: '1rem', marginBottom: '1.25rem' }}>
      <h3 style={{ margin: 0 }}>Our Prices</h3>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxWidth: '100%' }}>Location
        <select aria-label="Prices widget location" value={locationId} onChange={event => setLocationChoice(event.target.value)} disabled={!locations.length} style={{ padding: '0.5rem', maxWidth: '100%' }}>
          {!locations.length && <option value="">No locations available</option>}
          {locations.map(item => <option key={item.id} value={String(item.id)}>{item.name}{item.airportCode ? ` (${item.airportCode})` : ''}{item.isDefault ? ' - Default' : ''}</option>)}
        </select>
      </label>
    </div>
    {!locations.length && <p role="status">{locationError || 'No locations available.'} {locationSettingsLink}</p>}
    {locationId && !sessionId && <p role="alert">Sign in to load the prices widget.</p>}
    {locationId && sessionId && (!currentWidget || currentWidget.loading) && <p role="status">Loading prices widget...</p>}
    {currentWidget?.error && <div role="alert" style={{ color: '#b02a37', marginBottom: '1rem' }}>
      {currentWidget.error}{currentWidget.status === 409 && <> {locationSettingsLink}</>}
    </div>}
    {snippet && <>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontWeight: 600 }}>Code snippet
        <textarea ref={snippetRef} aria-label="Our Prices embed code" readOnly rows={4} value={snippet} style={{ width: '100%', boxSizing: 'border-box', padding: '0.75rem', resize: 'vertical', fontFamily: 'monospace', fontWeight: 400 }} />
      </label>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.75rem' }}>
        <button type="button" onClick={copySnippet}>Copy</button>
        {copyMessage && <span role="status">{copyMessage}</span>}
      </div>
      <h4 style={{ margin: '1.5rem 0 0.75rem' }}>Preview</h4>
      <iframe title="Our Prices preview" sandbox="allow-scripts" srcDoc={`<!doctype html><body style='font-family:arial;padding:16px'>${snippet}</body>`} style={{ width: '100%', height: '380px', boxSizing: 'border-box', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: 'white' }} />
    </>}
    <p>Prices come from aircraft and instructors marked 'Show on website', using this location's rates.</p>
    <p>Changes appear on the website within about a minute.</p>
    <details>
      <summary>Pricing details</summary>
      <p>Only aircraft and instructors with Show on website turned on and a rate are included. Existing aircraft and instructor rates are used, with location-specific rates from aircraft availability schedules or instructor work schedules taking precedence.</p>
      <p>At a non-default location, only aircraft and instructors with a schedule at that location are included.</p>
    </details>
  </section>;
};

export default OrganizationWebsite;